'use client';

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Check, Plus, Minus, ShoppingBag, ShieldCheck, FileSpreadsheet, Upload, Heart } from 'lucide-react';
import { BOX_BUILDER_OPTIONS } from '@/data/mockData';
import { useCommerce } from '@/context/CommerceContext';

export default function CustomBoxBuilder() {
  const { sizes, finishes, brandingOptions, greetingCards } = BOX_BUILDER_OPTIONS;
  const { addToCart, openRfqWithData, products } = useCommerce();
  const snackList = products;

  // State
  const [selectedSizeId, setSelectedSizeId] = useState(sizes[1].id); // default 5 snacks
  const [selectedFinishId, setSelectedFinishId] = useState(finishes[0].id); // Royal Vermilion & Gold
  const [selectedBrandingId, setSelectedBrandingId] = useState(brandingOptions[1].id);
  const [selectedCardId, setSelectedCardId] = useState(greetingCards[0].id);
  const [selectedSnackIds, setSelectedSnackIds] = useState<string[]>([]);

  React.useEffect(() => {
    if (products.length > 0 && selectedSnackIds.length === 0) {
      setSelectedSnackIds(products.slice(0, 3).map(p => p.id));
    }
  }, [products, selectedSnackIds.length]);
  const [quantity, setQuantity] = useState(25);
  const [customMessage, setCustomMessage] = useState('With Best Compliments & Auspicious Wishes');
  const [companyName, setCompanyName] = useState('Celebration Host');
  const [logoUploaded, setLogoUploaded] = useState(false);

  const currentSize = sizes.find(s => s.id === selectedSizeId) || sizes[0];
  const currentFinish = finishes.find(f => f.id === selectedFinishId) || finishes[0];
  const currentBranding = brandingOptions.find(b => b.id === selectedBrandingId) || brandingOptions[0];
  const currentCard = greetingCards.find(c => c.id === selectedCardId) || greetingCards[0];

  // Capacity calculations
  const capacity = currentSize.capacity;
  const currentCount = selectedSnackIds.length;
  const isFull = currentCount >= capacity;

  const toggleSnack = (id: string) => {
    if (selectedSnackIds.includes(id)) {
      setSelectedSnackIds(prev => prev.filter(item => item !== id));
    } else {
      if (selectedSnackIds.length < capacity) {
        setSelectedSnackIds(prev => [...prev, id]);
      }
    }
  };

  // Pricing calculations
  const pricing = useMemo(() => {
    const snackCostPerBox = selectedSnackIds.length * 140; // avg 150g portion
    const singleBoxBasePrice = currentSize.baseCost + currentFinish.cost + currentBranding.cost + currentCard.cost + snackCostPerBox;

    let discountPercent = 0;
    if (quantity >= 100) discountPercent = 20;
    else if (quantity >= 50) discountPercent = 15;
    else if (quantity >= 25) discountPercent = 10;
    else if (quantity >= 10) discountPercent = 5;

    const grossTotal = singleBoxBasePrice * quantity;
    const discountAmount = Math.round((grossTotal * discountPercent) / 100);
    const netSubtotal = grossTotal - discountAmount;
    const gstAmount = Math.round(netSubtotal * 0.05);
    const totalAmount = netSubtotal + gstAmount;

    return {
      singleBoxBasePrice,
      grossTotal,
      discountPercent,
      discountAmount,
      netSubtotal,
      gstAmount,
      totalAmount,
      snackCostPerBox,
      packagingCostPerBox: currentSize.baseCost + currentFinish.cost
    };
  }, [currentSize, currentFinish, currentBranding, currentCard, selectedSnackIds, quantity]);

  const handleAddBoxToCart = () => {
    addToCart({
      productId: `custom-box-${Date.now()}`,
      name: `Bespoke Box: ${currentSize.name} (${currentFinish.name})`,
      image: '/images/royal_gift_box.jpg',
      weight: `${quantity} Boxes`,
      unitPrice: Math.round(pricing.totalAmount / quantity),
      quantity: 1,
    });
  };

  return (
    <section id="box-builder" className="py-16 md:py-24 bg-[#FFFFFF] border-b border-[#DDDAC4]">
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
          <div className="inline-flex items-center space-x-1.5 px-3.5 py-1 rounded-full bg-[#950E09]/10 text-[#950E09] text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5 text-[#E09F55]" />
            <span>Interactive Box Configurator • Bespoke Keepsake Boxes</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0F382A] tracking-tight leading-[1.18]">
            Build Your Own Royal Heritage Snack Box
          </h2>
          <p className="text-sm sm:text-base text-[#171711]/70 font-sans">
            Select box capacity, choose handcrafted sweets and savouries, customize lid branding, and calculate dynamic tiered wholesale pricing in real-time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Interactive Steps (7 Cols) */}
          <div className="lg:col-span-7 space-y-8 bg-white/90 p-6 sm:p-8 rounded-3xl border border-[#DDDAC4] shadow-sm font-sans">
            
            {/* Step 1: Box Capacity Size */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="font-display font-bold text-sm sm:text-base text-[#950E09] flex items-center space-x-2">
                  <span className="w-6 h-6 rounded-full bg-[#950E09] text-[#F7F4D6] text-xs flex items-center justify-center font-bold">1</span>
                  <span>Select Box Capacity</span>
                </label>
                <span className="text-xs text-[#171711]/60">Select number of confections</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {sizes.map((s) => {
                  const isSelected = selectedSizeId === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        setSelectedSizeId(s.id);
                        if (selectedSnackIds.length > s.capacity) {
                          setSelectedSnackIds(prev => prev.slice(0, s.capacity));
                        }
                      }}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                        isSelected
                          ? 'border-[#950E09] bg-[#950E09]/5 ring-2 ring-[#950E09]/20 shadow-sm'
                          : 'border-[#DDDAC4] hover:bg-[#F7F4D6]/50 bg-white'
                      }`}
                    >
                      <div className="font-display font-bold text-xs sm:text-sm text-[#171711]">
                        {s.name}
                      </div>
                      <div className="text-[11px] text-[#387613] font-semibold mt-1">
                        Holds up to {s.capacity} items
                      </div>
                      <div className="text-[10px] text-[#171711]/60 mt-0.5">
                        Base: ₹{s.baseCost}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Box Finish Style */}
            <div className="space-y-3">
              <label className="font-display font-bold text-sm sm:text-base text-[#950E09] flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#950E09] text-[#F7F4D6] text-xs flex items-center justify-center font-bold">2</span>
                <span>Choose Packaging Finish & Craft</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {finishes.map((f) => {
                  const isSelected = selectedFinishId === f.id;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFinishId(f.id)}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                        isSelected
                          ? 'border-[#E09F55] bg-[#E09F55]/10 ring-2 ring-[#E09F55]/30'
                          : 'border-[#DDDAC4] hover:bg-[#F7F4D6]/50 bg-white'
                      }`}
                    >
                      <div className="font-display font-bold text-xs text-[#171711]">
                        {f.name}
                      </div>
                      <div className="text-[11px] text-[#950E09] font-bold mt-1">
                        +₹{f.cost} / box
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3: Choose Snacks with Visual Capacity Meter */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="font-display font-bold text-sm sm:text-base text-[#950E09] flex items-center space-x-2">
                  <span className="w-6 h-6 rounded-full bg-[#950E09] text-[#F7F4D6] text-xs flex items-center justify-center font-bold">3</span>
                  <span>Choose Confections ({currentCount} / {capacity} Selected)</span>
                </label>
                <span className={`text-xs font-bold ${isFull ? 'text-[#387613]' : 'text-[#950E09]'}`}>
                  {isFull ? '✓ Box Full' : `Pick ${capacity - currentCount} more`}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-[#DDDAC4] h-2 rounded-full overflow-hidden">
                <div
                  className="bg-[#387613] h-full transition-all duration-300"
                  style={{ width: `${(currentCount / capacity) * 100}%` }}
                ></div>
              </div>

              {/* Snacks selector grid */}
              {snackList.length === 0 ? (
                <div className="p-4 text-center text-xs text-neutral-500 bg-neutral-50 rounded-xl border border-neutral-200">
                  No delicacies available in catalog currently.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1">
                {snackList.map((prod) => {
                  const isChecked = selectedSnackIds.includes(prod.id);
                  const canSelect = isChecked || !isFull;

                  return (
                    <button
                      key={prod.id}
                      disabled={!canSelect}
                      onClick={() => toggleSnack(prod.id)}
                      className={`p-2.5 rounded-xl border text-left transition-all flex flex-col justify-between cursor-pointer ${
                        isChecked
                          ? 'border-[#387613] bg-[#387613]/10 ring-1 ring-[#387613]'
                          : canSelect
                          ? 'border-[#DDDAC4] hover:border-[#E09F55] bg-white'
                          : 'opacity-40 border-[#DDDAC4] bg-gray-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <span className="text-[11px] font-normal text-[#171711] line-clamp-1">
                          {prod.name}
                        </span>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ml-1 ${
                          isChecked ? 'bg-[#387613] text-white' : 'border border-gray-300'
                        }`}>
                          {isChecked ? '✓' : '+'}
                        </div>
                      </div>
                      <span className="text-[10px] text-[#387613] font-medium mt-1">
                        150g Portion
                      </span>
                    </button>
                  );
                })}
              </div>
              )}
            </div>

            {/* Step 4: Custom Branding & Greeting Card */}
            <div className="space-y-3 pt-2">
              <label className="font-display font-bold text-sm sm:text-base text-[#950E09] flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#950E09] text-[#F7F4D6] text-xs flex items-center justify-center font-bold">4</span>
                <span>Personalization, Logo & Greeting Card</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-[#171711] mb-1">Branding Foil/Sticker</div>
                  <select
                    value={selectedBrandingId}
                    onChange={(e) => setSelectedBrandingId(e.target.value)}
                    className="w-full text-xs p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  >
                    {brandingOptions.map(b => (
                      <option key={b.id} value={b.id}>{b.name} (+₹{b.cost})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs font-semibold text-[#171711] mb-1">Occasion Blessing Card</div>
                  <select
                    value={selectedCardId}
                    onChange={(e) => setSelectedCardId(e.target.value)}
                    className="w-full text-xs p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  >
                    {greetingCards.map(c => (
                      <option key={c.id} value={c.id}>{c.name} (+₹{c.cost})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message and Host/Company name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="text-[11px] font-semibold text-[#171711] block mb-1">
                    Event Host / Company Name:
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Sundaram Tech / Rajesh & Deepa"
                    className="w-full text-xs p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-[#171711] block mb-1">
                    Card Personal Message:
                  </label>
                  <input
                    type="text"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="e.g. Wishing you prosperity and festive joy"
                    className="w-full text-xs p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
              </div>

              {/* Logo Upload Simulation */}
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-[#F7F4D6]/60 border border-[#DDDAC4]">
                <button
                  type="button"
                  onClick={() => setLogoUploaded(!logoUploaded)}
                  className="px-3 py-1.5 rounded-lg bg-white border border-[#E09F55] text-xs font-bold text-[#950E09] flex items-center space-x-1.5 cursor-pointer hover:bg-[#E09F55]/10"
                >
                  <Upload className="w-3.5 h-3.5 text-[#E09F55]" />
                  <span>{logoUploaded ? '✓ Logo Attached' : 'Attach Company/Event Logo'}</span>
                </button>
                <span className="text-[11px] text-[#171711]/60">
                  {logoUploaded ? 'brand_logo_gold_emboss.svg ready' : 'Supports SVG, PNG, PDF for gold foil stamping'}
                </span>
              </div>
            </div>

          </div>

          {/* Right Column: Live Box Preview & Pricing Breakdown (5 Cols) */}
          <div className="lg:col-span-5 space-y-6 sticky top-24">
            
            {/* Box Visual Preview Card */}
            <div className="rounded-3xl bg-[#6F0A06] text-[#F7F4D6] p-6 border-2 border-[#E09F55] shadow-2xl relative overflow-hidden">
              <div className="relative rounded-2xl overflow-hidden mb-4 border border-[#E09F55]/40 shadow-inner aspect-[4/3]">
                <img
                  src="/images/royal_gift_box.jpg"
                  alt="Custom Royal Gift Hamper Box Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 bg-[#950E09]/90 text-[#F2BC6F] text-[10px] font-bold px-2 py-0.5 rounded border border-[#E09F55]/40 uppercase tracking-wider">
                  Live Preview
                </div>
              </div>

              {/* Box Specifications Badge */}
              <div className="space-y-1 mb-4">
                <span className="text-[10px] text-[#F2BC6F] font-bold uppercase tracking-widest font-sans">
                  Configured Luxury Box
                </span>
                <h4 className="font-display text-lg font-bold text-[#F7F4D6]">
                  {currentSize.name} • {currentFinish.name}
                </h4>
                <div className="text-xs text-[#DDDAC4] font-sans">
                  Personalized for: <strong className="text-[#F2BC6F]">{companyName}</strong>
                </div>
                <div className="text-[11px] text-[#DDDAC4]/80 italic font-sans">
                  &ldquo;{customMessage}&rdquo;
                </div>
              </div>

              {/* Quantity Stepper with Volume Tiers */}
              <div className="p-4 rounded-xl bg-black/30 border border-[#E09F55]/30 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#DDDAC4] font-medium font-sans">Order Quantity:</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setQuantity(q => Math.max(5, q - 5))}
                      className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold flex items-center justify-center cursor-pointer"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-display text-base font-bold text-[#F2BC6F] px-2">
                      {quantity} boxes
                    </span>
                    <button
                      onClick={() => setQuantity(q => q + 5)}
                      className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold flex items-center justify-center cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {pricing.discountPercent > 0 && (
                  <div className="text-[11px] text-[#387613] bg-[#F7F4D6] px-2.5 py-1 rounded font-bold flex items-center justify-between font-sans">
                    <span>Volume Discount Applied:</span>
                    <span>{pricing.discountPercent}% OFF</span>
                  </div>
                )}
              </div>

              {/* Itemized Price Calculation */}
              <div className="space-y-2 py-4 border-b border-[#E09F55]/20 text-xs font-sans">
                <div className="flex justify-between text-[#DDDAC4]">
                  <span>Single Box Base Cost:</span>
                  <span>₹{pricing.singleBoxBasePrice} / box</span>
                </div>
                <div className="flex justify-between text-[#DDDAC4]">
                  <span>Gross Total ({quantity} boxes):</span>
                  <span suppressHydrationWarning>₹{pricing.grossTotal.toLocaleString('en-IN')}</span>
                </div>
                {pricing.discountAmount > 0 && (
                  <div className="flex justify-between text-[#387613] bg-[#387613]/20 px-2 py-0.5 rounded font-bold">
                    <span>Discount Savings ({pricing.discountPercent}%):</span>
                    <span suppressHydrationWarning>-₹{pricing.discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#DDDAC4]">
                  <span>Packaged Food GST (5%):</span>
                  <span suppressHydrationWarning>₹{pricing.gstAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between items-baseline pt-2 text-[#F2BC6F] border-t border-[#E09F55]/30">
                  <span className="font-display font-bold text-sm">Estimated Total:</span>
                  <span className="font-display text-2xl font-black text-[#F7F4D6]" suppressHydrationWarning>
                    ₹{pricing.totalAmount.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-4">
                <button
                  onClick={handleAddBoxToCart}
                  className="w-full py-3.5 rounded-xl bg-[#E09F55] hover:bg-[#F2BC6F] text-[#171711] font-bold text-xs uppercase tracking-wider shadow-lg flex items-center justify-center space-x-2 transition-all cursor-pointer"
                >
                  <ShoppingBag className="w-4 h-4 text-[#950E09]" />
                  <span>Add {quantity} Custom Boxes to Cart</span>
                </button>

                <button
                  onClick={() => openRfqWithData({
                    boxConfiguration: {
                      size: currentSize.name,
                      finish: currentFinish.name,
                      branding: currentBranding.name,
                      card: currentCard.name,
                      quantity,
                      totalEstimate: pricing.totalAmount,
                      companyName,
                      customMessage
                    }
                  })}
                  className="w-full py-3 rounded-xl bg-transparent hover:bg-white/10 text-[#F7F4D6] border border-[#E09F55]/60 font-semibold text-xs flex items-center justify-center space-x-2 transition-all cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4 text-[#F2BC6F]" />
                  <span>Request Formal Sales Quotation (PDF)</span>
                </button>
              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
